import React, {useEffect, useMemo, useRef} from "react";
import {useSelector} from "react-redux";
import {css} from "@emotion/react";

import {Col, WideContent} from "@pg-design/grid-module";
import {mt, onDesktop, pb} from "@pg-design/helpers-css";
import {BrandSearchIcon} from "@pg-design/icons-module";
import {getThemeBreakpoint} from "@pg-design/styles-strings";
import {useElementWasVisible, usePrevious} from "@pg-mono/hooks";
import {debounce, isEmpty, range} from "@pg-mono/nodash";
import {RequestState} from "@pg-mono/request-state";
import {IApiRange} from "@pg-mono/rp-api-routes";
import {pluralize} from "@pg-mono/string-utils";
import {LISTING_VENDOR_TEST_IDS} from "@pg-mono/test-ids";
import {useUserDevice} from "@pg-mono/user-device";

import {IRPStore} from "../../../../../app/rp_reducer";
import {ColumnList} from "../../../../../atoms/ColumnList";
import {ColumnListItem} from "../../../../../atoms/ColumnListItem";
import {InfoPanel} from "../../../../../atoms/InfoPanel";
import {Country} from "../../../../../region/types/Country";
import {useSearchForm} from "../../../../../search/hooks/use_search_form";
import {SearchFormValuesType} from "../../../../../search/types/SearchFormValuesType";
import {TdaPortal} from "../../../../../smart_ad_server/components/TdaPortal";
import {SmartAdServerPlacement} from "../../../../../smart_ad_server/SmartAdServerPlacement";
import {offersVisibleHit} from "../../../../../tracking/algolytics_hits/offers_visible_hit";
import {OfferBoxWithOfferDetailsAction} from "../../../../atoms/OfferBoxWithOfferDetailsAction";
import {BudgetFilterBox} from "../../../../components/BudgetFilterBox";
import {RoomsFilterBox} from "../../../../components/RoomsFilterBox";
import {SmartAdSASTypes} from "../../../../helpers/smart_add_sas_types";
import {hitGTMOfferViewItemList} from "../../../../tracking/ecommerce_events/offer/hit_gtm_offer_view_item_list";
import {offerBoxTracking} from "../../../../tracking/offer_box_gtm_events";
import {IOfferListOffer} from "../../../../types/IOfferListOffer";
import {OfferType} from "../../../../types/OfferType";
import {ISelectedRegion} from "../../../actions/offer_list_selected_data_actions";
import {OFFER_LIST_PAGE_SIZE} from "../../../constants/offer_list";
import {getCountryId} from "../../../helpers/sub_type/get_fetch_offer_list_subfilter_query";
import {disableDynamicPositionProps, getDynamicPositionsCssSelector, useListDynamicPositions} from "../../../hooks/use_list_dynamic_positions";
import {usePageLinkBuilder} from "../../../hooks/usePageLinkBuilder";
import {IOfferListListStore} from "../../../reducers/desktop_offer_list_list_reducer";
import {OfferListSubType} from "../../../types/OfferListSubType";
import {DefaultOfferListingModal} from "../../DefaultOfferListingModal";
import {NextPageBox} from "../../NextPageBox";
import {OfferListSort} from "../../OfferListSort";
import {GetHomeLinkingBox} from "../GetHomeLinkingBox";
import {NotificationsListBox} from "../NotificationsListBox";
import {OfferListHeader} from "../OfferListHeader";
import {OfferListMidBanner} from "../OfferListMidBanner";
import {ArchivedOffersListing} from "./ArchivedOffersListing";

interface IProps {
    list: IOfferListListStore;
    metaDataTitle: string;
    selectedRegion: ISelectedRegion[];
    offerListSubType?: OfferListSubType;
}

export enum CreationListingPosition {
    SURPRISE_LONG = 17,
    SURPRISE_SHORT = 10
}

export const DefaultOfferListing = (props: IProps) => {
    const {count, offers, privileged_offers, page, sold_offers, sold_offers_count, sold_offers_page, requestState, latestQuery, page_size} = props.list;

    const searchFormValues = useSelector((state: IRPStore) => state.search.formValues) as SearchFormValuesType;
    const searchFormRooms = searchFormValues.rooms as IApiRange<number | "">;
    const buildPageLink = usePageLinkBuilder();
    const {isMobile} = useUserDevice();
    const prevRequestState = usePrevious(requestState, RequestState.None);
    const offerType = !!props.list.latestQuery.type ? parseInt(props.list.latestQuery.type) : undefined;
    const currentCountry = getCountryId(props.list.latestQuery.country);
    const isAbroadListing = currentCountry && currentCountry !== Country.POLAND;
    const lastPage = props.list.count ? Math.ceil(props.list.count / OFFER_LIST_PAGE_SIZE) : 1;

    const offerRequestReady = prevRequestState === RequestState.Waiting && requestState === RequestState.Success;
    const currentPage = sold_offers_count ? page + sold_offers_page - 1 : page;
    const privilegedOfferIds = privileged_offers.map(({id}) => id);
    const offersToRender = [...privileged_offers, ...(isEmpty(privileged_offers) ? offers : offers.filter(({id}) => !privilegedOfferIds.includes(id)))];
    const foundOffers = offersToRender.filter((offer) => offer.has_required_prices || offer.has_required_prices === null);
    const offersOnPreviousPagesAmount = (props.list.page - 1) * props.list.page_size;
    const remainingOffers = props.list.count - offersOnPreviousPagesAmount - foundOffers.length;

    const shouldShowRoomsFilterBox = foundOffers.length !== 0 && count >= 24 && searchFormRooms.upper === "" && searchFormRooms.lower === "";
    const shouldShowBudgetFilterBox = foundOffers.length !== 0 && count >= 24 && !searchFormValues.instalment && !searchFormValues.price.upper;

    const seenOffers = useRef<number[]>([]);
    const roomsFilterBoxPosition = 4;
    const budgetFilterBoxPosition = shouldShowRoomsFilterBox ? roomsFilterBoxPosition + 3 : roomsFilterBoxPosition;
    const priceFilterUsed = Boolean(latestQuery.price_1 || latestQuery.instalment);
    const priceDividerOfferIndex = offers.findIndex((el) => el.has_required_prices !== true) || 0;
    const dividedOffersCount = priceFilterUsed ? offers.slice(priceDividerOfferIndex, offers.length).length : 0;

    const debouncedOffersVisibleHit = debounce(() => {
        offersVisibleHit(seenOffers.current);

        seenOffers.current = [];
    }, 500);

    const offersToRenderIds = useMemo(() => [...offersToRender.map(({id}) => id.toString())], [offersToRender]);
    const offerToRenderIndexes = useMemo(() => {
        const indexes: {[key: string]: number} = {};
        offersToRender.forEach((offer, index) => {
            indexes[offer.id] = index;
        });
        return indexes;
    }, [offersToRender]);

    useElementWasVisible({
        ids: offersToRenderIds,
        indexes: offerToRenderIndexes,
        onElementVisible: (id, _, index) => {
            seenOffers.current = [...seenOffers.current, parseInt(id)];
            index !== undefined && offerBoxTracking.gtm.investTileView(parseInt(id), index);
            debouncedOffersVisibleHit();
        },
        once: true
    });

    const {wrapperProps, initializeDynamicPosition, getDynamicPositionProps} = useListDynamicPositions<HTMLDivElement>();

    // Commented out due to RP-217 requirements
    // const midListingReels = useMemo(() => {
    //     return <ListingReels selectedRegions={props.selectedRegion} className="listing-reels" />;
    // }, [props.selectedRegion]);

    const {openLocationSearch} = useSearchForm();
    const handleHeaderClick = () => {
        openLocationSearch();
    };

    const prevMetaDataTitle = useRef<string>(null);
    const gtmMeta = {
        listId: "listing",
        listName: props.metaDataTitle.split(" - ")[0],
        source: "listing" as const,
        tileEvents: {
            imageClick: offerBoxTracking.gtm.investTileImageClick,
            buttonClick: offerBoxTracking.gtm.investTileButtonClick,
            linkClick: offerBoxTracking.gtm.investTileLinkClick,
            logoClick: offerBoxTracking.gtm.investTileLogoClick
        }
    };
    useEffect(() => {
        if (requestState === RequestState.Success && prevMetaDataTitle.current !== props.metaDataTitle) {
            prevMetaDataTitle.current = props.metaDataTitle;
            hitGTMOfferViewItemList({
                offers: offersToRender,
                listId: gtmMeta.listId,
                listName: gtmMeta.listName
            });
        }
    }, [requestState, offersToRenderIds.join(","), props.metaDataTitle]);

    const renderListSection = useMemo(() => {
        let hasPriceDivider = false;

        const renderDivider = (has_required_prices: boolean | null) => {
            const shouldHaveDivider = !hasPriceDivider && has_required_prices === false;
            if (shouldHaveDivider) {
                hasPriceDivider = true;
            }
            return (
                shouldHaveDivider && (
                    <Col sm={4} md={12} lg={16} css={dividerStyles}>
                        <InfoPanel
                            icon={BrandSearchIcon}
                            heading="Znaleźliśmy także inne oferty, które mogą nie spełniać Twoich oczekiwań cenowych."
                            subHeading="Szczegółowych informacji udzielają deweloperzy"
                        />
                    </Col>
                )
            );
        };

        const getRegion = () => {
            const regionId = latestQuery.region && latestQuery.region?.length > 0 ? +latestQuery.region[latestQuery.region.length - 1] : undefined;
            const selectedRegionData = props.selectedRegion.find((region) => region.id === regionId);

            if (regionId && props.selectedRegion.length > 0) {
                return {
                    id: regionId,
                    name: selectedRegionData?.name,
                    slug: selectedRegionData?.slug,
                    parentSlug: selectedRegionData?.parent?.slug,
                    type: selectedRegionData?.type
                };
            }
            return {id: regionId, name: undefined, slug: undefined, parentSlug: undefined, type: undefined};
        };
        const region = getRegion();

        let suffix = 1;
        initializeDynamicPosition();

        return (
            <>
                {!isMobile && (!currentCountry || currentCountry === Country.POLAND) && (
                    <TdaPortal>
                        <SmartAdServerPlacement adFormat={SmartAdSASTypes.TDA} />
                    </TdaPortal>
                )}
                <DefaultOfferListingModal />

                <OfferListHeader
                    offerCount={props.list.count - dividedOffersCount}
                    propertiesCount={props.list.propertiesCount}
                    selectedRegion={props.selectedRegion}
                    latestQuery={latestQuery}
                    offerListSubType={props.offerListSubType}
                    onHeaderClick={handleHeaderClick}
                />

                {/*<WideContent css={[display("none"), onDesktop(display("block"), mb(4), w100)]}>*/}
                {/*    <SmartAdServerPlacement adFormat={SmartAdSASTypes.HERO} fetchPriority="high" preventGtmOnMobile />*/}
                {/*</WideContent>*/}

                <WideContent>
                    <div css={offerListControlsStyle}>
                        {/* @deprecated Comment code releated with displaying "Mieszkania i domy" tab due to RP-217 requirements
                        this can be delted entirely in future if we will keep this approach */}
                        {/* <ListingTypeTabs
                            filters={searchFormValues}
                            offerType={offerType}
                            currentListingType={ListingType.OFFERS}
                            additionalParams={{sort: props.list.latestQuery.sort}}
                        /> */}
                        <OfferListSort type="select" name="offer-sort" latestQuerySort={props.list.latestQuery.sort} css={offerListSortWrapperStyle} />
                    </div>
                </WideContent>
                <ColumnList {...wrapperProps} css={listDynamicRowOrderStyle}>
                    {offersToRender.map((offer: IOfferListOffer, idx: number) => {
                        const position = idx + 1;
                        const isShortListCase = offersToRender.length < 3 && position === offersToRender.length; // list is short, so detect the last element
                        const isLastPage = props.list.page === lastPage;
                        const isFifthLastIndex = position === offersToRender.length - 5;
                        const isLastPosition = position === offersToRender.length;

                        const surpriseAdPosition =
                            offersToRender.length >= CreationListingPosition.SURPRISE_LONG
                                ? CreationListingPosition.SURPRISE_LONG
                                : CreationListingPosition.SURPRISE_SHORT;

                        return (
                            <React.Fragment key={offer.id}>
                                {renderDivider(offer.has_required_prices)}
                                <ColumnListItem data-testid={LISTING_VENDOR_TEST_IDS.FIRST_LISTING_ELEMENT} dynamicProps={getDynamicPositionProps()}>
                                    <OfferBoxWithOfferDetailsAction
                                        offer={offer}
                                        latestQuery={props.list.latestQuery}
                                        index={idx}
                                        gtmMeta={gtmMeta}
                                        dataTestid={LISTING_VENDOR_TEST_IDS.PROPERTY_CONTAINER}
                                    />
                                </ColumnListItem>
                                {/*{position === 2 && isMobile && <SmartAdServerPlacement adFormat={SmartAdSASTypes.HERO} />}*/}
                                {/* Hidden due to RP-217 requirements */}
                                {/* {position === 2 && midListingReels} */}
                                {position === 2 && isMobile && <SmartAdServerPlacement adFormat={SmartAdSASTypes.TDA} />}
                                {(isShortListCase || position === 3) && <SmartAdServerPlacement adFormat={SmartAdSASTypes.OFFER_OF_THE_MONTH} />}
                                {(isShortListCase || position === 3 || position === 7 || position === 11) && (
                                    <SmartAdServerPlacement adFormat={SmartAdSASTypes.MONO_NATIVE_ADD} suffix={suffix++} />
                                )}

                                {isFifthLastIndex && <SmartAdServerPlacement adFormat={SmartAdSASTypes.IMAGE} />}
                                {position === surpriseAdPosition && (
                                    <SmartAdServerPlacement
                                        adFormat={SmartAdSASTypes.SURPRISE}
                                        referenceElementId={LISTING_VENDOR_TEST_IDS.FIRST_LISTING_ELEMENT}
                                    />
                                )}
                                {position === 5 && <OfferListMidBanner />}
                                {isLastPosition && !isLastPage && (
                                    <ColumnListItem {...getDynamicPositionProps()}>
                                        <NextPageBox
                                            nextPageLink={buildPageLink(props.list.page + 1)}
                                            elementsToVisit={{
                                                amount: remainingOffers,
                                                name: pluralize(["inwestycję", "inwestycje", "inwestycji"])(remainingOffers)
                                            }}
                                            referenceElementId={LISTING_VENDOR_TEST_IDS.FIRST_LISTING_ELEMENT}
                                        />
                                    </ColumnListItem>
                                )}
                                {shouldShowRoomsFilterBox && position === roomsFilterBoxPosition && (
                                    <ColumnListItem
                                        dynamicProps={isMobile ? disableDynamicPositionProps() : getDynamicPositionProps()}
                                        className="rp-filters-tile"
                                    >
                                        <RoomsFilterBox />
                                    </ColumnListItem>
                                )}
                                {shouldShowBudgetFilterBox && position === (isMobile ? budgetFilterBoxPosition - 1 : budgetFilterBoxPosition) && (
                                    <ColumnListItem dynamicProps={isMobile ? disableDynamicPositionProps() : getDynamicPositionProps()}>
                                        <BudgetFilterBox />
                                    </ColumnListItem>
                                )}
                                {isLastPage &&
                                    isLastPosition && ( // only on last page, last position
                                        <>
                                            {region.name && (
                                                <ColumnListItem dynamicProps={getDynamicPositionProps()}>
                                                    <NotificationsListBox regionName={region.name} />
                                                </ColumnListItem>
                                            )}
                                            {offerType !== OfferType.COMMERCIAL && !isAbroadListing && (
                                                <ColumnListItem dynamicProps={getDynamicPositionProps()}>
                                                    <GetHomeLinkingBox offerType={offerType} region={region} />
                                                </ColumnListItem>
                                            )}
                                        </>
                                    )}
                            </React.Fragment>
                        );
                    })}
                </ColumnList>

                {sold_offers_count ? <ArchivedOffersListing offers={sold_offers} dividerVisible={currentPage === Math.ceil(count / page_size)} /> : ""}
            </>
        );
    }, [offersToRender, offerRequestReady, props.metaDataTitle]);

    return renderListSection;
};

//  Styles
const dividerStyles = css`
    padding: 3rem 0;
`;

const offerListControlsStyle = css`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    ${pb(2)};
    ${mt(2)};

    ${onDesktop(css`
        ${pb(3)};
        ${mt(0)};
    `)};

    > div {
        ${pb(0)};
    }
`;

const offerListSortWrapperStyle = css`
    display: none;

    ${onDesktop(css`
        display: block;
        flex: 0 0 16rem;
    `)};
`;

const listDynamicRowOrderStyle = css`
    @media (max-width: ${getThemeBreakpoint().md}) {
        ${getDynamicPositionsCssSelector(range(0, 2))} {
            order: -4;
        }

        .listing-reels {
            order: -3;
        }

        ${getDynamicPositionsCssSelector(range(2, 4))} {
            order: -2;
        }

        .rp-filters-tile {
            order: -1;
        }
    }

    @media (min-width: ${getThemeBreakpoint().md}) {
        ${getDynamicPositionsCssSelector(range(0, 6))} {
            order: -4;
        }

        .listing-reels {
            order: -3;
        }

        ${getDynamicPositionsCssSelector(range(6, 12))} {
            order: -2;
        }

        .listing-banner {
            order: -1;
        }
    }

    @media (min-width: ${getThemeBreakpoint().lg}) {
        ${getDynamicPositionsCssSelector(range(0, 8))} {
            order: -4;
        }
        ${getDynamicPositionsCssSelector(range(8, 16))} {
            order: -2;
        }
    }
`;
import React, {useEffect, useMemo, useRef} from "react";
import {useSelector} from "react-redux";
import {css} from "@emotion/react";

import {Col, WideContent} from "@pg-design/grid-module";
import {mt, onDesktop, pb} from "@pg-design/helpers-css";
import {BrandSearchIcon} from "@pg-design/icons-module";
import {getThemeBreakpoint} from "@pg-design/styles-strings";
import {useElementWasVisible, usePrevious} from "@pg-mono/hooks";
import {debounce, isEmpty, range} from "@pg-mono/nodash";
import {RequestState} from "@pg-mono/request-state";
import {IApiRange} from "@pg-mono/rp-api-routes";
import {pluralize} from "@pg-mono/string-utils";
import {LISTING_VENDOR_TEST_IDS} from "@pg-mono/test-ids";
import {useUserDevice} from "@pg-mono/user-device";

import {IRPStore} from "../../../../../app/rp_reducer";
import {ColumnList} from "../../../../../atoms/ColumnList";
import {ColumnListItem} from "../../../../../atoms/ColumnListItem";
import {InfoPanel} from "../../../../../atoms/InfoPanel";
import {Country} from "../../../../../region/types/Country";
import {useSearchForm} from "../../../../../search/hooks/use_search_form";
import {SearchFormValuesType} from "../../../../../search/types/SearchFormValuesType";
import {TdaPortal} from "../../../../../smart_ad_server/components/TdaPortal";
import {SmartAdServerPlacement} from "../../../../../smart_ad_server/SmartAdServerPlacement";
import {offersVisibleHit} from "../../../../../tracking/algolytics_hits/offers_visible_hit";
import {OfferBoxWithOfferDetailsAction} from "../../../../atoms/OfferBoxWithOfferDetailsAction";
import {BudgetFilterBox} from "../../../../components/BudgetFilterBox";
import {RoomsFilterBox} from "../../../../components/RoomsFilterBox";
import {SmartAdSASTypes} from "../../../../helpers/smart_add_sas_types";
import {hitGTMOfferViewItemList} from "../../../../tracking/ecommerce_events/offer/hit_gtm_offer_view_item_list";
import {offerBoxTracking} from "../../../../tracking/offer_box_gtm_events";
import {IOfferListOffer} from "../../../../types/IOfferListOffer";
import {OfferType} from "../../../../types/OfferType";
import {ISelectedRegion} from "../../../actions/offer_list_selected_data_actions";
import {OFFER_LIST_PAGE_SIZE} from "../../../constants/offer_list";
import {getCountryId} from "../../../helpers/sub_type/get_fetch_offer_list_subfilter_query";
import {disableDynamicPositionProps, getDynamicPositionsCssSelector, useListDynamicPositions} from "../../../hooks/use_list_dynamic_positions";
import {usePageLinkBuilder} from "../../../hooks/usePageLinkBuilder";
import {IOfferListListStore} from "../../../reducers/desktop_offer_list_list_reducer";
import {OfferListSubType} from "../../../types/OfferListSubType";
import {DefaultOfferListingModal} from "../../DefaultOfferListingModal";
import {NextPageBox} from "../../NextPageBox";
import {OfferListSort} from "../../OfferListSort";
import {GetHomeLinkingBox} from "../GetHomeLinkingBox";
import {NotificationsListBox} from "../NotificationsListBox";
import {OfferListHeader} from "../OfferListHeader";
import {OfferListMidBanner} from "../OfferListMidBanner";
import {ArchivedOffersListing} from "./ArchivedOffersListing";

interface IProps {
    list: IOfferListListStore;
    metaDataTitle: string;
    selectedRegion: ISelectedRegion[];
    offerListSubType?: OfferListSubType;
}

export enum CreationListingPosition {
    SURPRISE_LONG = 17,
    SURPRISE_SHORT = 10
}

export const DefaultOfferListing = (props: IProps) => {
    const {count, offers, privileged_offers, page, sold_offers, sold_offers_count, sold_offers_page, requestState, latestQuery, page_size} = props.list;

    const searchFormValues = useSelector((state: IRPStore) => state.search.formValues) as SearchFormValuesType;
    const searchFormRooms = searchFormValues.rooms as IApiRange<number | "">;
    const buildPageLink = usePageLinkBuilder();
    const {isMobile} = useUserDevice();
    const prevRequestState = usePrevious(requestState, RequestState.None);
    const offerType = !!props.list.latestQuery.type ? parseInt(props.list.latestQuery.type) : undefined;
    const currentCountry = getCountryId(props.list.latestQuery.country);
    const isAbroadListing = currentCountry && currentCountry !== Country.POLAND;
    const lastPage = props.list.count ? Math.ceil(props.list.count / OFFER_LIST_PAGE_SIZE) : 1;

    const offerRequestReady = prevRequestState === RequestState.Waiting && requestState === RequestState.Success;
    const currentPage = sold_offers_count ? page + sold_offers_page - 1 : page;
    const privilegedOfferIds = privileged_offers.map(({id}) => id);
    const offersToRender = [...privileged_offers, ...(isEmpty(privileged_offers) ? offers : offers.filter(({id}) => !privilegedOfferIds.includes(id)))];
    const foundOffers = offersToRender.filter((offer) => offer.has_required_prices || offer.has_required_prices === null);
    const offersOnPreviousPagesAmount = (props.list.page - 1) * props.list.page_size;
    const remainingOffers = props.list.count - offersOnPreviousPagesAmount - foundOffers.length;

    const shouldShowRoomsFilterBox = foundOffers.length !== 0 && count >= 24 && searchFormRooms.upper === "" && searchFormRooms.lower === "";
    const shouldShowBudgetFilterBox = foundOffers.length !== 0 && count >= 24 && !searchFormValues.instalment && !searchFormValues.price.upper;

    const seenOffers = useRef<number[]>([]);
    const roomsFilterBoxPosition = 4;
    const budgetFilterBoxPosition = shouldShowRoomsFilterBox ? roomsFilterBoxPosition + 3 : roomsFilterBoxPosition;
    const priceFilterUsed = Boolean(latestQuery.price_1 || latestQuery.instalment);
    const priceDividerOfferIndex = offers.findIndex((el) => el.has_required_prices !== true) || 0;
    const dividedOffersCount = priceFilterUsed ? offers.slice(priceDividerOfferIndex, offers.length).length : 0;

    const debouncedOffersVisibleHit = debounce(() => {
        offersVisibleHit(seenOffers.current);

        seenOffers.current = [];
    }, 500);

    const offersToRenderIds = useMemo(() => [...offersToRender.map(({id}) => id.toString())], [offersToRender]);
    const offerToRenderIndexes = useMemo(() => {
        const indexes: {[key: string]: number} = {};
        offersToRender.forEach((offer, index) => {
            indexes[offer.id] = index;
        });
        return indexes;
    }, [offersToRender]);

    useElementWasVisible({
        ids: offersToRenderIds,
        indexes: offerToRenderIndexes,
        onElementVisible: (id, _, index) => {
            seenOffers.current = [...seenOffers.current, parseInt(id)];
            index !== undefined && offerBoxTracking.gtm.investTileView(parseInt(id), index);
            debouncedOffersVisibleHit();
        },
        once: true
    });

    const {wrapperProps, initializeDynamicPosition, getDynamicPositionProps} = useListDynamicPositions<HTMLDivElement>();

    // Commented out due to RP-217 requirements
    // const midListingReels = useMemo(() => {
    //     return <ListingReels selectedRegions={props.selectedRegion} className="listing-reels" />;
    // }, [props.selectedRegion]);

    const {openLocationSearch} = useSearchForm();
    const handleHeaderClick = () => {
        openLocationSearch();
    };

    const prevMetaDataTitle = useRef<string>(null);
    const gtmMeta = {
        listId: "listing",
        listName: props.metaDataTitle.split(" - ")[0],
        source: "listing" as const,
        tileEvents: {
            imageClick: offerBoxTracking.gtm.investTileImageClick,
            buttonClick: offerBoxTracking.gtm.investTileButtonClick,
            linkClick: offerBoxTracking.gtm.investTileLinkClick,
            logoClick: offerBoxTracking.gtm.investTileLogoClick
        }
    };
    useEffect(() => {
        if (requestState === RequestState.Success && prevMetaDataTitle.current !== props.metaDataTitle) {
            prevMetaDataTitle.current = props.metaDataTitle;
            hitGTMOfferViewItemList({
                offers: offersToRender,
                listId: gtmMeta.listId,
                listName: gtmMeta.listName
            });
        }
    }, [requestState, offersToRenderIds.join(","), props.metaDataTitle]);

    const renderListSection = useMemo(() => {
        let hasPriceDivider = false;

        const renderDivider = (has_required_prices: boolean | null) => {
            const shouldHaveDivider = !hasPriceDivider && has_required_prices === false;
            if (shouldHaveDivider) {
                hasPriceDivider = true;
            }
            return (
                shouldHaveDivider && (
                    <Col sm={4} md={12} lg={16} css={dividerStyles}>
                        <InfoPanel
                            icon={BrandSearchIcon}
                            heading="Znaleźliśmy także inne oferty, które mogą nie spełniać Twoich oczekiwań cenowych."
                            subHeading="Szczegółowych informacji udzielają deweloperzy"
                        />
                    </Col>
                )
            );
        };

        const getRegion = () => {
            const regionId = latestQuery.region && latestQuery.region?.length > 0 ? +latestQuery.region[latestQuery.region.length - 1] : undefined;
            const selectedRegionData = props.selectedRegion.find((region) => region.id === regionId);

            if (regionId && props.selectedRegion.length > 0) {
                return {
                    id: regionId,
                    name: selectedRegionData?.name,
                    slug: selectedRegionData?.slug,
                    parentSlug: selectedRegionData?.parent?.slug,
                    type: selectedRegionData?.type
                };
            }
            return {id: regionId, name: undefined, slug: undefined, parentSlug: undefined, type: undefined};
        };
        const region = getRegion();

        let suffix = 1;
        initializeDynamicPosition();

        return (
            <>
                {!isMobile && (!currentCountry || currentCountry === Country.POLAND) && (
                    <TdaPortal>
                        <SmartAdServerPlacement adFormat={SmartAdSASTypes.TDA} />
                    </TdaPortal>
                )}
                <DefaultOfferListingModal />

                <OfferListHeader
                    offerCount={props.list.count - dividedOffersCount}
                    propertiesCount={props.list.propertiesCount}
                    selectedRegion={props.selectedRegion}
                    latestQuery={latestQuery}
                    offerListSubType={props.offerListSubType}
                    onHeaderClick={handleHeaderClick}
                />

                {/*<WideContent css={[display("none"), onDesktop(display("block"), mb(4), w100)]}>*/}
                {/*    <SmartAdServerPlacement adFormat={SmartAdSASTypes.HERO} fetchPriority="high" preventGtmOnMobile />*/}
                {/*</WideContent>*/}

                <WideContent>
                    <div css={offerListControlsStyle}>
                        {/* @deprecated Comment code releated with displaying "Mieszkania i domy" tab due to RP-217 requirements
                        this can be delted entirely in future if we will keep this approach */}
                        {/* <ListingTypeTabs
                            filters={searchFormValues}
                            offerType={offerType}
                            currentListingType={ListingType.OFFERS}
                            additionalParams={{sort: props.list.latestQuery.sort}}
                        /> */}
                        <OfferListSort type="select" name="offer-sort" latestQuerySort={props.list.latestQuery.sort} css={offerListSortWrapperStyle} />
                    </div>
                </WideContent>
                <ColumnList {...wrapperProps} css={listDynamicRowOrderStyle}>
                    {offersToRender.map((offer: IOfferListOffer, idx: number) => {
                        const position = idx + 1;
                        const isShortListCase = offersToRender.length < 3 && position === offersToRender.length; // list is short, so detect the last element
                        const isLastPage = props.list.page === lastPage;
                        const isFifthLastIndex = position === offersToRender.length - 5;
                        const isLastPosition = position === offersToRender.length;

                        const surpriseAdPosition =
                            offersToRender.length >= CreationListingPosition.SURPRISE_LONG
                                ? CreationListingPosition.SURPRISE_LONG
                                : CreationListingPosition.SURPRISE_SHORT;

                        return (
                            <React.Fragment key={offer.id}>
                                {renderDivider(offer.has_required_prices)}
                                <ColumnListItem data-testid={LISTING_VENDOR_TEST_IDS.FIRST_LISTING_ELEMENT} dynamicProps={getDynamicPositionProps()}>
                                    <OfferBoxWithOfferDetailsAction
                                        offer={offer}
                                        latestQuery={props.list.latestQuery}
                                        index={idx}
                                        gtmMeta={gtmMeta}
                                        dataTestid={LISTING_VENDOR_TEST_IDS.PROPERTY_CONTAINER}
                                    />
                                </ColumnListItem>
                                {/*{position === 2 && isMobile && <SmartAdServerPlacement adFormat={SmartAdSASTypes.HERO} />}*/}
                                {/* Hidden due to RP-217 requirements */}
                                {/* {position === 2 && midListingReels} */}
                                {position === 2 && isMobile && <SmartAdServerPlacement adFormat={SmartAdSASTypes.TDA} />}
                                {(isShortListCase || position === 3) && <SmartAdServerPlacement adFormat={SmartAdSASTypes.OFFER_OF_THE_MONTH} />}
                                {(isShortListCase || position === 3 || position === 7 || position === 11) && (
                                    <SmartAdServerPlacement adFormat={SmartAdSASTypes.MONO_NATIVE_ADD} suffix={suffix++} />
                                )}

                                {isFifthLastIndex && <SmartAdServerPlacement adFormat={SmartAdSASTypes.IMAGE} />}
                                {position === surpriseAdPosition && (
                                    <SmartAdServerPlacement
                                        adFormat={SmartAdSASTypes.SURPRISE}
                                        referenceElementId={LISTING_VENDOR_TEST_IDS.FIRST_LISTING_ELEMENT}
                                    />
                                )}
                                {position === 5 && <OfferListMidBanner />}
                                {isLastPosition && !isLastPage && (
                                    <ColumnListItem {...getDynamicPositionProps()}>
                                        <NextPageBox
                                            nextPageLink={buildPageLink(props.list.page + 1)}
                                            elementsToVisit={{
                                                amount: remainingOffers,
                                                name: pluralize(["inwestycję", "inwestycje", "inwestycji"])(remainingOffers)
                                            }}
                                            referenceElementId={LISTING_VENDOR_TEST_IDS.FIRST_LISTING_ELEMENT}
                                        />
                                    </ColumnListItem>
                                )}
                                {shouldShowRoomsFilterBox && position === roomsFilterBoxPosition && (
                                    <ColumnListItem
                                        dynamicProps={isMobile ? disableDynamicPositionProps() : getDynamicPositionProps()}
                                        className="rp-filters-tile"
                                    >
                                        <RoomsFilterBox />
                                    </ColumnListItem>
                                )}
                                {shouldShowBudgetFilterBox && position === (isMobile ? budgetFilterBoxPosition - 1 : budgetFilterBoxPosition) && (
                                    <ColumnListItem dynamicProps={isMobile ? disableDynamicPositionProps() : getDynamicPositionProps()}>
                                        <BudgetFilterBox />
                                    </ColumnListItem>
                                )}
                                {isLastPage &&
                                    isLastPosition && ( // only on last page, last position
                                        <>
                                            {region.name && (
                                                <ColumnListItem dynamicProps={getDynamicPositionProps()}>
                                                    <NotificationsListBox regionName={region.name} />
                                                </ColumnListItem>
                                            )}
                                            {offerType !== OfferType.COMMERCIAL && !isAbroadListing && (
                                                <ColumnListItem dynamicProps={getDynamicPositionProps()}>
                                                    <GetHomeLinkingBox offerType={offerType} region={region} />
                                                </ColumnListItem>
                                            )}
                                        </>
                                    )}
                            </React.Fragment>
                        );
                    })}
                </ColumnList>

                {sold_offers_count ? <ArchivedOffersListing offers={sold_offers} dividerVisible={currentPage === Math.ceil(count / page_size)} /> : ""}
            </>
        );
    }, [offersToRender, offerRequestReady, props.metaDataTitle]);

    return renderListSection;
};

//  Styles
const dividerStyles = css`
    padding: 3rem 0;
`;

const offerListControlsStyle = css`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    ${pb(2)};
    ${mt(2)};

    ${onDesktop(css`
        ${pb(3)};
        ${mt(0)};
    `)};

    > div {
        ${pb(0)};
    }
`;

const offerListSortWrapperStyle = css`
    display: none;

    ${onDesktop(css`
        display: block;
        flex: 0 0 16rem;
    `)};
`;

const listDynamicRowOrderStyle = css`
    @media (max-width: ${getThemeBreakpoint().md}) {
        ${getDynamicPositionsCssSelector(range(0, 2))} {
            order: -4;
        }

        .listing-reels {
            order: -3;
        }

        ${getDynamicPositionsCssSelector(range(2, 4))} {
            order: -2;
        }

        .rp-filters-tile {
            order: -1;
        }
    }

    @media (min-width: ${getThemeBreakpoint().md}) {
        ${getDynamicPositionsCssSelector(range(0, 6))} {
            order: -4;
        }

        .listing-reels {
            order: -3;
        }

        ${getDynamicPositionsCssSelector(range(6, 12))} {
            order: -2;
        }

        .listing-banner {
            order: -1;
        }
    }

    @media (min-width: ${getThemeBreakpoint().lg}) {
        ${getDynamicPositionsCssSelector(range(0, 8))} {
            order: -4;
        }
        ${getDynamicPositionsCssSelector(range(8, 16))} {
            order: -2;
        }
    }
`;
